import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, Box, IconButton, CircularProgress, Typography, Backdrop, ButtonGroup } from '@mui/material';
import CloseOutlined from '@mui-symbols-material/w400/CloseOutlined';
import ZoomInOutlined from '@mui-symbols-material/w400/ZoomInOutlined';
import ZoomOutOutlined from '@mui-symbols-material/w400/ZoomOutOutlined';
import DownloadOutlined from '@mui-symbols-material/w400/DownloadOutlined';
import { ReportImage } from '@models/DomainModels/Master/report-image';
import styles from './FullSizeImageViewer.module.scss';
import { logger } from '@utils/logging-utils';

/**
 * Constants for zoom and viewport calculations
 */
const ZOOM_CONSTANTS = {
  FACTOR: 1.25,
  MAX_SCALE: 5,
  MIN_SCALE: 0.25,
  VIEWPORT_WIDTH_RATIO: 0.9,
  VIEWPORT_HEIGHT_RATIO: 0.8,
  MIN_INITIAL_SCALE: 0.1
} as const;

/**
 * Props for FullSizeImageViewer component
 */
interface FullSizeImageViewerProps {
  /** Whether the viewer is open */
  readonly open: boolean;
  /** Base64 image data */
  readonly imageData: string;
  /** Image information */
  readonly imageInfo?: ReportImage;
  /** Whether the image is loading */
  readonly loading: boolean;
  /** Callback when the viewer is closed */
  readonly onClose: () => void;
}

/**
 * Calculate initial scale based on viewport and image dimensions
 * @param viewportWidth - Available viewport width
 * @param viewportHeight - Available viewport height
 * @param imageWidth - Actual image width
 * @param imageHeight - Actual image height
 * @returns Optimal initial scale factor
 */
const calculateInitialScale = (viewportWidth: number, viewportHeight: number, imageWidth: number, imageHeight: number): number => {
  const availableWidth = viewportWidth * ZOOM_CONSTANTS.VIEWPORT_WIDTH_RATIO;
  const availableHeight = viewportHeight * ZOOM_CONSTANTS.VIEWPORT_HEIGHT_RATIO;

  const scaleByWidth = availableWidth / imageWidth;
  const scaleByHeight = availableHeight / imageHeight;

  const initialScale = Math.min(scaleByWidth, scaleByHeight);
  return Math.max(ZOOM_CONSTANTS.MIN_INITIAL_SCALE, Math.min(initialScale, 1));
};

/**
 * Full-size image viewer with zoom, download, and close functionality
 */
export const FullSizeImageViewer: React.FC<Readonly<FullSizeImageViewerProps>> = ({ open, imageData, imageInfo, loading, onClose }) => {
  const { t } = useTranslation(['reportImages']);
  const [scale, setScale] = useState<number>(1);

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Handle image load to calculate optimal initial scale
   */
  const handleImageLoad = useCallback(() => {
    if (!imageRef.current || !containerRef.current) {
      return;
    }

    const image = imageRef.current;
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();

    const optimalScale = calculateInitialScale(containerRect.width, containerRect.height, image.naturalWidth, image.naturalHeight);

    setScale(optimalScale);
    logger.info('FullSizeImageViewer', `Image loaded: ${image.naturalWidth}x${image.naturalHeight}, initial scale: ${optimalScale.toFixed(3)}`);
  }, []);

  // Reset scale when dialog opens, but defer to handleImageLoad for optimal scaling
  useEffect(() => {
    if (open) {
      // Initialize with a reasonable default that handleImageLoad will correct
      setScale(ZOOM_CONSTANTS.MIN_INITIAL_SCALE);
    }
  }, [open]);

  /**
   * Handle zoom in
   */
  const handleZoomIn = useCallback(() => {
    setScale(prev => Math.min(prev * ZOOM_CONSTANTS.FACTOR, ZOOM_CONSTANTS.MAX_SCALE));
  }, []);

  /**
   * Handle zoom out
   */
  const handleZoomOut = useCallback(() => {
    setScale(prev => Math.max(prev / ZOOM_CONSTANTS.FACTOR, ZOOM_CONSTANTS.MIN_SCALE));
  }, []);

  /**
   * Handle download
   */
  const handleDownload = useCallback(() => {
    if (!imageData || !imageInfo) {
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = imageData;
      // Use 'report-image.jpg' if filename contains a dot, otherwise use original name or fallback
      const downloadName = imageInfo.name && !imageInfo.name.includes('.') ? imageInfo.name : 'report-image.jpg';
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      logger.info('FullSizeImageViewer', `Downloaded image: ${downloadName}`);
    } catch (error) {
      logger.error('FullSizeImageViewer', `Error downloading image: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [imageData, imageInfo]);

  /**
   * Handle wheel zoom
   */
  const handleWheel = useCallback(
    (event: WheelEvent) => {
      event.preventDefault();
      if (event.deltaY < 0) {
        handleZoomIn();
      } else {
        handleZoomOut();
      }
    },
    [handleZoomIn, handleZoomOut]
  );

  // Add wheel event listener with passive: false to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !open) {
      return;
    }

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [open, handleWheel]);

  /**
   * Handle keyboard events for accessibility
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      className={styles.fullSizeImageDialog}
      slotProps={{ paper: { className: styles.dialogPaper } }}
      onKeyDown={handleKeyDown}>
      <Backdrop open={open} className={styles.backdrop} onClick={onClose}>
        <Box onClick={e => e.stopPropagation()} className={styles.mainContainer}>
          {/* Visually hidden title for screen readers */}
          <Typography id="image-viewer-title" variant="h1" className={styles.visuallyHidden}>
            {t('reportImages.fullSizeViewer')}
          </Typography>

          {/* Control bar */}
          <Box className={styles.controlBar}>
            <ButtonGroup variant="text" className={styles.buttonGroup}>
              <IconButton onClick={handleZoomIn} className={styles.groupedButton} disabled={scale >= ZOOM_CONSTANTS.MAX_SCALE}>
                <ZoomInOutlined />
              </IconButton>

              <IconButton onClick={handleZoomOut} className={styles.groupedButton} disabled={scale <= ZOOM_CONSTANTS.MIN_SCALE}>
                <ZoomOutOutlined />
              </IconButton>

              <IconButton onClick={handleDownload} className={styles.groupedButton} disabled={!imageData}>
                <DownloadOutlined />
              </IconButton>
            </ButtonGroup>

            <IconButton onClick={onClose} className={styles.controlButton} data-testid="fullsize-image-viewer-close-button">
              <CloseOutlined />
            </IconButton>
          </Box>

          {/* Image container */}
          <DialogContent ref={containerRef} className={styles.imageContainer}>
            {loading && (
              <Box className={styles.loadingContainer}>
                <CircularProgress />
                <Typography variant="body1" className={styles.loadingText}>
                  {t('reportImages.loadingFullSize')}
                </Typography>
              </Box>
            )}
            {!loading && imageData && (
              <Box className={styles.imageWrapper}>
                <img
                  ref={imageRef}
                  src={imageData}
                  alt={imageInfo?.name ?? 'Full size image'}
                  className={styles.fullSizeImage}
                  style={{
                    transform: `scale(${scale})`
                  }}
                  onLoad={handleImageLoad}
                  draggable={false}
                  data-testid="full-size-image"
                />
              </Box>
            )}
            {!loading && !imageData && (
              <Box className={styles.errorContainer}>
                <Typography variant="body1" color="error">
                  {t('reportImages.errorLoadingImage')}
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Box>
      </Backdrop>
    </Dialog>
  );
};
